import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {QUERY_KEYS} from '../../utils/queryKeys';
import i18n from '../../i18n';
import {IPresentationMeta} from '../../incase-domain/presentation';
import {setPresentationValidationErrorsAction, updateSelectedPresentationAction} from '../../store/actions/presentation';
import {startPresUpdateSpinnerAction, stopPresUpdateSpinnerAction} from '../../store/actions/spinner';
import {selectedPresentationSelector} from '../../store/selectors/presentation.selectors';
import {updatePresentation} from '../presentation';
import {presentationValidator} from '../../incase-domain/validators/presentation';
import {ZodValidationError} from '../../utils/ZodValidationError';
import {formatErrors} from '../../utils/validation';
import {IValidationErrorResponse} from '../../incase-domain/common/validationErrorResponse';

export const UPDATE_PRESENTATION_KEY = ['updatePresentationMeta'];

function useUpdatePresentation(): UseMutationResult<
    IPresentationMeta & {errors: Record<string, string[]>},
    unknown,
    {presentation: IPresentationMeta; isSlideUpdate?: boolean | undefined},
    void
> {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const selectedPresentation = useSelector(selectedPresentationSelector);

    const validatePresentation = (presentation: IPresentationMeta): void => {
        const validation = presentationValidator.safeParse(presentation);
        if (!validation.success) throw new ZodValidationError(validation.error.errors);
    };

    return useMutation(
        (args: {presentation: IPresentationMeta; isSlideUpdate?: boolean}) => {
            validatePresentation(args.presentation);
            return updatePresentation(args.presentation, args.isSlideUpdate);
        },
        {
            mutationKey: UPDATE_PRESENTATION_KEY,
            onMutate: () => {
                dispatch(startPresUpdateSpinnerAction(true));
            },
            onSettled: () => {
                dispatch(stopPresUpdateSpinnerAction());
            },
            onSuccess: (result) => {
                dispatch(updateSelectedPresentationAction({...selectedPresentation, ...result}));
                dispatch(setPresentationValidationErrorsAction({}));

                queryClient.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
                toast.success(i18n.t('toasts.successfullySaved'));
            },
            onError: (error) => {
                if (axios.isAxiosError(error)) {
                    const errorData = (error as AxiosError<Record<string, string[]>>).response?.data;

                    if (errorData) {
                        dispatch(setPresentationValidationErrorsAction(errorData));
                    } else {
                        toast.error(i18n.t('toasts.errorOccurred'));
                    }
                } else if (error instanceof ZodValidationError) {
                    const errorData: IValidationErrorResponse = {
                        message: error.message,
                        locale: 'toasts.validationError',
                        errors: formatErrors(error.errors),
                    };

                    dispatch(setPresentationValidationErrorsAction(errorData.errors as Record<string, string[]>));
                }
            },
        }
    );
}

export default useUpdatePresentation;
